// The panel's Excel download for a client whose list is its whole CUADRE (`list: all`, Bancamía).
//
// The operator's own workbook is the Matriz de Conciliación: a blank first row, the headings on
// row 2, one row per office from row 3, Century Gothic 8, thin borders, a colour per band of
// headings and a number format per column. A download that does not look like it is one they
// re-format by hand every morning before pasting it under yesterday's rows. So the panel config
// says how each column looks (`export:` in `panel/clients/<code>.yaml`, served with the fields)
// and this file only applies it — nothing here names a client or a column of the CUADRE.
//
// Two halves, for the reason the rest of `utils/` is pure: `hojaDeMatriz` decides every cell and
// every style from the panel's data, with no library, and is what the tests pin; `libroDeMatriz`
// hands that to exceljs, which is loaded only when somebody actually downloads.

import { nombreOficinaDeMainKey, numeroDeCelda } from "./cierrePanel";
import { parseDateOnly } from "./dateUtils";

// The Matriz's own numbers: a thousands separator, negatives in red, no decimals.
const FORMATO_NUMERO = "#,##0;[Red]#,##0";
const ANCHO = 13;

// A, B, C and D come from the panel row itself and AK from the alert notes, so their look is the
// same for every client and is not served with the fields.
const IDENTIDAD = [
  { clave: "fecha", header: "FECHA", width: 11.29, numFmt: "mm-dd-yy" },
  { clave: "codigo", header: "CODIGO", width: 13 },
  { clave: "regional", header: "REGIONAL", width: 18.29 },
  { clave: "oficina", header: "OFICINA", width: 35.71 },
];
// B-175 — this Matriz is the report that goes to the client, and its OBSERVACIONES carries only the
// justifications the team wrote when attending the alerts (`notasDelEquipo`), never what the system
// found.
const OBSERVACIONES = { clave: "observaciones", header: "OBSERVACIONES", width: 53 };

// `YYYY-MM-DD` as an Excel date — built in UTC, so no browser zone can move it to the day before.
function fechaExcel(fechaCuadre) {
  const iso = parseDateOnly(fechaCuadre);
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// An office code as the Matriz stores it: a number when it is one (201), the text otherwise.
function codigoExcel(codigo) {
  if (codigo === null || codigo === undefined || codigo === "") return null;
  return /^\d+$/.test(String(codigo)) ? Number(codigo) : String(codigo);
}

/**
 * Every cell and every style of the download, decided without a library.
 *
 * @param {object}   options
 * @param {object[]} options.columnas  the list's columns (`columnasDeLista`), each with `excel`
 * @param {object[]} options.filas     the panel's rows (`filaDeCierre`), in the order to write them
 * @param {?object}  options.config    the served top-level `export` — sheet, font, heading colour
 */
export function hojaDeMatriz({ columnas = [], filas = [], config = null }) {
  const headerFill = config?.headerFill || null;

  const columnasDeHoja = [
    ...IDENTIDAD.map((c) => ({ ...c, fill: headerFill, cellFill: null })),
    ...columnas.map((c) => ({
      clave: c.fieldName,
      header: c.excel?.header || c.etiqueta,
      width: c.excel?.width || ANCHO,
      fill: c.excel?.fill || headerFill,
      cellFill: c.excel?.cellFill || null,
      numFmt: c.excel?.numFormat || FORMATO_NUMERO,
    })),
    { ...OBSERVACIONES, fill: headerFill, cellFill: null },
  ];

  const filasDeHoja = filas.map((f) => {
    const raw = f.raw || {};
    return [
      fechaExcel(raw.fecha_cuadre ?? f.fecha),
      codigoExcel(raw.codigo_oficina ?? f.codigo),
      f.regional ? String(f.regional).toUpperCase() : null,
      f.oficina || (raw.main_key ? nombreOficinaDeMainKey(raw.main_key) : null),
      // Absent stays an empty cell, never a zero — the panel's rule, carried into the file.
      ...columnas.map((c) => numeroDeCelda(raw[c.fieldName])),
      f.notasMatriz || null,
    ];
  });

  return {
    hoja: config?.sheet || "Hoja1",
    archivo: nombreDeArchivo(config?.filename, filas),
    fuente: { name: config?.font || "Calibri", size: config?.fontSize || 11 },
    columnas: columnasDeHoja,
    filas: filasDeHoja,
  };
}

// `YYYY-MM-DD` as `DD-MM-YY`. Dashes, not slashes: a `/` is not allowed in a file name, and the
// browser would quietly swap it for `_`.
function fechaCorta(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y.slice(-2)}`;
}

// "<name> 21-09-26.xlsx" for one day, "<name> 21-08-26 a 21-09-26.xlsx" for a range — the oldest
// and the newest day among the rows exported, whatever order they are in.
function nombreDeArchivo(nombre, filas) {
  const base = nombre || "cierres";
  const dias = [...new Set(filas.map((f) => parseDateOnly(f.raw?.fecha_cuadre ?? f.fecha)).filter(Boolean))].sort();
  if (!dias.length) return `${base}.xlsx`;
  const desde = fechaCorta(dias[0]);
  const hasta = fechaCorta(dias[dias.length - 1]);
  return desde === hasta ? `${base} ${desde}.xlsx` : `${base} ${desde} a ${hasta}.xlsx`;
}

const BORDE = { style: "thin", color: { argb: "FF000000" } };
export const BORDES = { top: BORDE, left: BORDE, bottom: BORDE, right: BORDE };
export const relleno = (rgb) => ({ type: "pattern", pattern: "solid", fgColor: { argb: `FF${rgb}` } });

// The 1-based column number as Excel letters: 1 -> A, 27 -> AA.
export function letraDeColumna(n) {
  let s = "";
  for (let i = n; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + ((i - 1) % 26)) + s;
  return s;
}

/**
 * The sheet as an exceljs workbook. `ExcelJS` is passed in, so a test hands it the real library
 * and the screen loads it only on a click.
 */
export function libroDeMatriz(ExcelJS, hoja) {
  const libro = new ExcelJS.Workbook();
  const ws = libro.addWorksheet(hoja.hoja, { views: [{ state: "frozen", ySplit: 2 }] });
  const fuente = hoja.fuente;

  hoja.columnas.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.width;
  });

  // Row 1 is blank in the Matriz; the headings are row 2.
  const encabezado = ws.getRow(2);
  encabezado.height = 18.75;
  hoja.columnas.forEach((c, i) => {
    const celda = encabezado.getCell(i + 1);
    celda.value = c.header;
    celda.font = { ...fuente, bold: true };
    celda.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    celda.border = BORDES;
    if (c.fill) celda.fill = relleno(c.fill);
  });

  hoja.filas.forEach((valores, r) => {
    const fila = ws.getRow(r + 3);
    valores.forEach((valor, i) => {
      const c = hoja.columnas[i];
      const celda = fila.getCell(i + 1);
      celda.value = valor;
      celda.font = fuente;
      celda.border = BORDES;
      if (c.numFmt) celda.numFmt = c.numFmt;
      if (c.cellFill) celda.fill = relleno(c.cellFill);
    });
  });

  const ultima = letraDeColumna(hoja.columnas.length);
  ws.autoFilter = `A2:${ultima}${Math.max(2, hoja.filas.length + 2)}`;
  return libro;
}

// Build and hand the browser the file. exceljs is loaded here, on the click, not with the panel.
export async function descargarMatriz(hoja) {
  const [{ default: ExcelJS }, { saveAs }] = await Promise.all([import("exceljs"), import("file-saver")]);
  const buffer = await libroDeMatriz(ExcelJS, hoja).xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    hoja.archivo
  );
}
